import { AssignmentInd, Close, Refresh } from '@mui/icons-material'
import { Button, CircularProgress, Grid2, IconButton, styled, TextField, Typography } from '@mui/material'
import { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import api from '../../../utils/axios';
import BranchCard from '../../../components/Cards/BranchCard';
import { IBranch, ITranslation } from 'sierra';
import { useApp } from '../../../contexts/AppContext';
import { useAuth } from '../../../hooks/useAuth';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});
interface OutletContextType {
    setOpenAssignManagers: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenAssignEmployees: React.Dispatch<React.SetStateAction<boolean>>;
}

const BranchUpdate = memo(() => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { lang, translate } = useApp()
    const location = useLocation()
    const { setOpenAssignManagers, setOpenAssignEmployees } = useOutletContext<OutletContextType>()

    if (!location.state?.branch) navigate('/system/branches')
    const branch: IBranch = useMemo(() => location.state?.branch, [location])

    const [branchName, setBranchName] = useState<ITranslation | { en: '', ar: '', zh: '' }>(branch?.branch_name || { en: '', ar: '', zh: '' })
    const [branchHeaderImage, setBranchHeaderImage] = useState<string | null | undefined>(branch?.branch_header_url || '')
    const [branchLogoImage, setBranchLogoImage] = useState<string | null | undefined>(branch?.branch_logo_url || '')
    const [apiCall, setApiCall] = useState<boolean | undefined>(false)

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUpdateBranch = useCallback(() => {
        setApiCall(true)
        api.post(`branches/${branch?.branch_id}`, {
            branch_name: branchName,
            branch_header_image: branchHeaderImage == branch?.branch_header_url ? null : branchHeaderImage,
            branch_logo_image: branchLogoImage == branch?.branch_logo_url ? null : branchLogoImage,
        }, { meta: { showSnackbar: true } }).then(() => {
            setApiCall(false)
            // clearForm()
        }).catch((err) => {
            console.log(err);
            setApiCall(false)
            // clearForm()
        })
    }, [branchName, branchHeaderImage, branchLogoImage, branch])

    const readUploadFile = useCallback((e: React.ChangeEvent<HTMLInputElement>, setStateFunction: Dispatch<SetStateAction<string | null | undefined>>) => {
        e.preventDefault();
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            const reader = new FileReader();
            reader.readAsDataURL(selectedFile);
            reader.onload = (e) => {
                const data = e.target?.result as string | null | undefined;
                setStateFunction(data);
            };
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    }, [])

    const resetData = useCallback(() => {
        setBranchName(branch?.branch_name || { en: '', ar: '', zh: '' });
        setBranchHeaderImage(branch?.branch_header_url || '');
        setBranchLogoImage(branch?.branch_logo_url || '');
    }, [branch])


    useEffect(() => {
        resetData()
    }, [location, resetData])


    return (
        <Grid2 container size={'grow'} height={'100%'} padding={2} spacing={4} flexDirection={'column'}>

            <Grid2 container size={'auto'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>

                <Grid2>
                    <Typography variant='h5'>
                        {translate("Update Branch")}
                    </Typography>
                </Grid2>

                <Grid2>
                    <IconButton onClick={() => {
                        setOpenAssignManagers(false)
                        navigate('/system/branches')
                    }}>
                        <Close />
                    </IconButton>
                </Grid2>

            </Grid2>

            <Grid2 container size={'grow'} flexDirection={'column'} spacing={2} flexWrap={'nowrap'} sx={{ overflowX: 'hidden', overflowY: 'auto' }}>

                <Grid2>
                    <BranchCard hideButtons={true} branch={{
                        branch_id: branch.branch_id || 0,
                        branch_name: branchName[lang] ? branchName : { en: '', ar: '', zh: '' },
                        branch_header_url: branchHeaderImage || 'https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png',
                        branch_logo_url: branchLogoImage || 'https://www.svgrepo.com/show/508699/landscape-placeholder.svg',
                        created_by: 1,
                        branch_managers: [],
                        creation_timestamp: new Date('2025-01-01'),
                        is_deleted: false
                    }} />
                </Grid2>

                <Grid2>
                    <TextField
                        fullWidth
                        label={translate('Branch Name')}
                        variant={'outlined'}
                        size='small'
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                        value={branchName[lang] || ''}
                        onChange={e => setBranchName(b => ({ ...b, [lang]: e.target.value }))}
                    />

                </Grid2>

                <Grid2 container flexDirection={{ xs: 'column', sm: 'row' }}>
                    <Grid2 size={'grow'}>
                        <Button component="label" fullWidth variant='outlined' sx={{ borderRadius: 2, fontWeight: 'bold', borderWidth: '2px' }}>
                            {branchHeaderImage ? translate('Edit Header Image') : `${translate('Upload Header Image')} +`}
                            <VisuallyHiddenInput
                                accept="image/*" // Limit to only accept image files
                                required
                                type="file"
                                onChange={e => readUploadFile(e, setBranchHeaderImage)} />
                        </Button>
                    </Grid2>
                    <Grid2 size={'grow'}>
                        <Button component="label" fullWidth variant='outlined' sx={{ borderRadius: 2, fontWeight: 'bold', borderWidth: '2px' }}>
                            {branchLogoImage ? translate('Edit Logo Image') : `${translate('Upload Logo Image')} +`}
                            <VisuallyHiddenInput
                                accept="image/*" // Limit to only accept image files
                                required
                                type="file"
                                onChange={e => readUploadFile(e, setBranchLogoImage)} />
                        </Button>
                    </Grid2>
                </Grid2>

                <Grid2>
                    <Button variant='contained' fullWidth size='large' onClick={handleUpdateBranch}
                        endIcon={apiCall && <CircularProgress sx={{ color: 'white' }} />}
                        disabled={apiCall || !branchName[lang] || !branchHeaderImage || !branchLogoImage ||
                            (branchName == branch?.branch_name && branchLogoImage == branch.branch_logo_url && branchHeaderImage == branch.branch_header_url)}
                    >{translate("Update Branch")}</Button>
                </Grid2>
                <Grid2>
                    <Button variant='outlined' fullWidth size='large' onClick={resetData} sx={{ fontWeight: 'bold', borderWidth: '2px' }}
                        endIcon={<Refresh />}
                    >{translate("Reset Changes")}</Button>
                </Grid2>

                <Grid2 display={user?.role === 'admin' ? 'flex' : 'none'}>
                    <Button variant='contained' fullWidth size='large' onClick={() => setOpenAssignManagers(true)}
                        endIcon={<AssignmentInd />}
                    >{translate("Assign Managers")}</Button>
                </Grid2>

                <Grid2 display={['manager', 'admin'].includes(user?.role || '') ? 'flex' : 'none'}>
                    <Button variant='contained' fullWidth size='large' onClick={() => setOpenAssignEmployees(true)}
                        endIcon={<AssignmentInd />}
                    >{translate("Assign Employees")}</Button>
                </Grid2>

            </Grid2>

        </Grid2>
    )
})

export default BranchUpdate